import type { IrohAddress, IrohConnection, IrohEndpoint } from "@codeshare/iroh";
import type { SignalPayload, Unsubscribe } from "@codeshare/signaling";
import { Channel } from "./channel";
import { IrohDataChannel } from "./iroh-channel";
import type { ChannelOptions, PeerState } from "./peer";
import { CONTROL_LABEL } from "./peer";
import type { ConnectionStats } from "./stats";

export interface IrohPeerOptions {
  selfId: string;
  peerId: string;
  /** This side's endpoint (bound to a relay). */
  endpoint: IrohEndpoint;
  /** Give up ("failed") if not connected this long after starting. */
  connectTimeoutMs?: number;
}

/**
 * A connection to one peer through an iroh relay, used when WebRTC can't connect. Same surface
 * as Peer: a control channel, feature channels, states. The impolite side dials (once it knows
 * the other side's address) and opens the control channel; the polite side accepts both.
 */
export class IrohPeer {
  readonly kind = "iroh" as const;
  readonly selfId: string;
  readonly peerId: string;
  /** Same rule as Peer, so both sides agree on who dials. */
  readonly polite: boolean;
  readonly control = new Channel();

  readonly #endpoint: IrohEndpoint;
  readonly #stateListeners = new Set<(state: PeerState) => void>();
  readonly #channelListeners = new Set<(channel: Channel) => void>();
  #state: PeerState = "connecting";
  #remote: IrohAddress | undefined;
  #connection: IrohConnection | undefined;
  #dialing = false;
  #controlAttached = false;
  #connectTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(options: IrohPeerOptions) {
    this.selfId = options.selfId;
    this.peerId = options.peerId;
    this.polite = options.selfId > options.peerId;
    this.#endpoint = options.endpoint;
    this.#connectTimer = setTimeout(() => {
      if (this.#state === "connecting") this.#setState("failed");
    }, options.connectTimeoutMs ?? 15_000);
  }

  get state(): PeerState {
    return this.#state;
  }

  /** Relayed connections are only tried with peers that answered over WebRTC first. */
  get answered(): boolean {
    return true;
  }

  onAnswered(_listener: () => void): Unsubscribe {
    return () => {};
  }

  get localEndpointId(): string {
    return this.#endpoint.id;
  }

  /** The other side's id: from the connection once there is one (authenticated by QUIC). */
  get remoteEndpointId(): string | undefined {
    return this.#connection?.remoteId ?? this.#remote?.endpointId;
  }

  get relayUrl(): string {
    return this.#endpoint.relayUrl;
  }

  get connected(): boolean {
    return this.#connection !== undefined;
  }

  onStateChange(listener: (state: PeerState) => void): Unsubscribe {
    this.#stateListeners.add(listener);
    return () => this.#stateListeners.delete(listener);
  }

  /** Channels the remote side opened (not the control channel). */
  onChannel(listener: (channel: Channel) => void): Unsubscribe {
    this.#channelListeners.add(listener);
    return () => this.#channelListeners.delete(listener);
  }

  /** Where to reach the other side. The impolite side dials as soon as it knows. */
  setRemote(address: IrohAddress): void {
    if (this.#state === "closed" || this.#state === "failed") return;
    this.#remote = address;
    if (!this.polite && !this.#connection && !this.#dialing) void this.#dial(address);
  }

  /** The other side dialed us (the polite side accepts). */
  accept(connection: IrohConnection): void {
    if (this.#connection || this.#state === "closed" || this.#state === "failed") {
      connection.close(0, "duplicate");
      return;
    }
    this.#attach(connection);
  }

  openChannel(label: string, protocol: string, _options: ChannelOptions = {}): Channel {
    const connection = this.#connection;
    if (!connection) throw new Error("not connected");
    return new Channel(IrohDataChannel.open(connection.openStream(), { label, protocol }));
  }

  /** Signaling is only used to find each other; nothing to do once relayed. */
  async handleSignal(_payload: SignalPayload): Promise<void> {}

  async stats(): Promise<ConnectionStats> {
    return { transport: "iroh", relayUrl: this.relayUrl, channels: [] };
  }

  close(): void {
    if (this.#state === "closed") return;
    clearTimeout(this.#connectTimer);
    this.control.close();
    this.#connection?.close(0, "closed");
    this.#setState("closed");
  }

  async #dial(address: IrohAddress): Promise<void> {
    this.#dialing = true;
    try {
      const connection = await this.#endpoint.connect(address);
      if (this.#state !== "connecting") {
        connection.close(0, "closed");
        return;
      }
      this.#attach(connection);
    } catch {
      if (this.#state === "connecting") this.#setState("failed");
    } finally {
      this.#dialing = false;
    }
  }

  #attach(connection: IrohConnection): void {
    clearTimeout(this.#connectTimer);
    this.#connection = connection;
    if (!this.polite) {
      this.#controlAttached = true;
      this.control.attach(
        IrohDataChannel.open(connection.openStream(), { label: CONTROL_LABEL, protocol: "" }),
      );
    }
    void this.#acceptStreams(connection);
    void connection.closed.then(() => {
      // Lost, or closed by the other side: either way this peer is over. (A deliberate leave
      // shows up first as the control channel closing.)
      if (this.#state !== "closed") this.#setState("failed");
    });
    this.#setState("connected");
  }

  async #acceptStreams(connection: IrohConnection): Promise<void> {
    for (;;) {
      let channel: IrohDataChannel;
      try {
        channel = await IrohDataChannel.accept(await connection.acceptStream());
      } catch {
        return; // connection closed
      }
      if (this.#state === "closed") return channel.close();
      if (channel.label === CONTROL_LABEL && this.polite && !this.#controlAttached) {
        this.#controlAttached = true;
        this.control.attach(channel);
        continue;
      }
      const wrapped = new Channel(channel);
      for (const listener of this.#channelListeners) listener(wrapped);
    }
  }

  #setState(state: PeerState): void {
    if (state === this.#state || this.#state === "closed") return;
    this.#state = state;
    for (const listener of this.#stateListeners) listener(state);
  }
}
